import {
  compareLocalDate,
  localDate,
  localDateParts,
  type LocalDate,
  plusDays,
  plusMonths,
  plusWeeks,
  plusYears,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from '@taler/domain'

export const REPORT_BUCKETS = ['DAY', 'WEEK', 'MONTH', 'YEAR'] as const
export type ReportBucket = (typeof REPORT_BUCKETS)[number]

export interface ReportRange {
  readonly from: LocalDate
  readonly until: LocalDate
  readonly bucket: ReportBucket
}

/** The most buckets a window may split into; a finer bucket that would exceed it is not offered. */
const MAX_BUCKETS = 400

const BUCKET_START: Record<ReportBucket, (value: LocalDate) => LocalDate> = {
  DAY: (value) => value,
  WEEK: startOfWeek,
  MONTH: startOfMonth,
  YEAR: startOfYear,
}

const BUCKET_ADVANCE: Record<ReportBucket, (value: LocalDate) => LocalDate> = {
  DAY: (value) => plusDays(value, 1),
  WEEK: (value) => plusWeeks(value, 1),
  MONTH: (value) => plusMonths(value, 1),
  YEAR: (value) => plusYears(value, 1),
}

/** The calendar year holding `today`, the window a report opens on. */
export function defaultReportRange(today: LocalDate): ReportRange {
  const { year } = localDateParts(today)
  return { from: localDate(year, 1, 1), until: localDate(year, 12, 31), bucket: 'MONTH' }
}

/** The bucket start every period in the window is keyed by, oldest first. */
export function bucketStarts(range: ReportRange): LocalDate[] {
  if (!bucketFits(range, range.bucket)) return []
  const starts: LocalDate[] = []
  for (
    let cursor = BUCKET_START[range.bucket](range.from);
    compareLocalDate(cursor, range.until) <= 0;
    cursor = BUCKET_ADVANCE[range.bucket](cursor)
  ) {
    starts.push(cursor)
  }
  return starts
}

/** Whether `window` splits into at most `MAX_BUCKETS` buckets of `bucket`. */
export function bucketFits(window: Pick<ReportRange, 'from' | 'until'>, bucket: ReportBucket): boolean {
  let count = 0
  for (
    let cursor = BUCKET_START[bucket](window.from);
    compareLocalDate(cursor, window.until) <= 0;
    cursor = BUCKET_ADVANCE[bucket](cursor)
  ) {
    count += 1
    if (count > MAX_BUCKETS) return false
  }
  return true
}

/** The buckets `window` can be shown at, finest first. */
export function fittingBuckets(window: Pick<ReportRange, 'from' | 'until'>): ReportBucket[] {
  return REPORT_BUCKETS.filter((bucket) => bucketFits(window, bucket))
}

/** The same-length window one step before (-1) or after (+1) `range`. */
export function steppedWindow(
  range: Pick<ReportRange, 'from' | 'until'>,
  direction: -1 | 1,
): Pick<ReportRange, 'from' | 'until'> {
  const days = range.until.epochDay - range.from.epochDay + 1
  return {
    from: plusDays(range.from, direction * days),
    until: plusDays(range.until, direction * days),
  }
}
